import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import {
    AccountTreeRounded,
    TableRowsRounded,
    ViewKanbanRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import ObjectIcon from "../components/ObjectIcon";
import { Button } from "../components/Buttons";
import { useObjects } from "../context/ObjectsContext";
import {
    createPipeline,
    getObjectBoard,
    listPipelines,
    transitionRecord,
} from "../api";
import { apiError } from "../utils/meta";

const initials = (name) =>
    (name || "?")
        .split(/\s+/)
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

function RecordCard({ record, titleKey, onDragStart }) {
    const title = record.data?.[titleKey] || "Untitled";
    return (
        <Stack
            draggable
            onDragStart={(e) => onDragStart(e, record)}
            sx={{
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-mid)",
                borderRadius: "5px",
                p: "12px 14px",
                gap: 1,
                cursor: "grab",
                boxShadow: "0 1px 4px rgba(23,23,21,0.05)",
                "&:active": { cursor: "grabbing" },
            }}
        >
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                {title}
            </Typography>
            {record.ownerName && (
                <Stack
                    sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                >
                    <Avatar
                        sx={{
                            width: 22,
                            height: 22,
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: "3px",
                            background: "var(--bg-tint)",
                            color: "var(--text-strong-muted)",
                        }}
                    >
                        {initials(record.ownerName)}
                    </Avatar>
                    <Typography
                        sx={{ fontSize: 12, color: "var(--text-secondary)" }}
                    >
                        {record.ownerName}
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
}

export default function ObjectBoard() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { objects, loading: objectsLoading } = useObjects();
    const object = objects.find((o) => o.slug === slug) ?? null;

    const [board, setBoard] = useState(null);
    const [noPipeline, setNoPipeline] = useState(false);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [dragOver, setDragOver] = useState(null);

    const titleKey =
        [...(object?.fields ?? [])]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .find((f) => f.type === "text")?.key ??
        object?.fields?.[0]?.key;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getObjectBoard(slug);
            if (data?.pipeline) {
                setBoard(data);
                setNoPipeline(false);
            } else {
                setNoPipeline(true);
            }
        } catch {
            /* No pipeline yet (or nothing to board) — show the empty state. */
            setNoPipeline(true);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        load();
    }, [load]);

    async function handleCreatePipeline() {
        if (!object) return;
        setCreating(true);
        try {
            /* Reuse an existing pipeline if one exists but the board 404'd. */
            const { data: pipelines } = await listPipelines(object.id);
            if (!Array.isArray(pipelines) || pipelines.length === 0) {
                await createPipeline(object.id, {
                    name: `${object.name} pipeline`,
                });
            }
            navigate(`/o/${slug}/pipeline`);
        } catch (err) {
            toast.error(apiError(err, "Could not create the pipeline"));
        } finally {
            setCreating(false);
        }
    }

    function onDragStart(e, record) {
        e.dataTransfer.setData("text/plain", record.id);
        e.dataTransfer.effectAllowed = "move";
    }

    async function onDrop(e, toStage) {
        e.preventDefault();
        setDragOver(null);
        const recordId = e.dataTransfer.getData("text/plain");
        if (!recordId) return;

        /* Optimistic move; on 400/403 toast the reason and snap back. */
        const prev = board;
        let moved = null;
        const columns = board.columns.map((col) => {
            const idx = col.records.findIndex((r) => r.id === recordId);
            if (idx === -1) return col;
            moved = col.records[idx];
            return {
                ...col,
                records: col.records.filter((r) => r.id !== recordId),
            };
        });
        if (!moved || moved.stageId === toStage.id) return;
        setBoard({
            ...board,
            columns: columns.map((col) =>
                col.stage.id === toStage.id
                    ? {
                          ...col,
                          records: [
                              { ...moved, stageId: toStage.id },
                              ...col.records,
                          ],
                      }
                    : col,
            ),
        });

        try {
            await transitionRecord(slug, recordId, toStage.id);
            load();
        } catch (err) {
            setBoard(prev);
            toast.error(apiError(err, "That move isn't allowed"));
            load();
        }
    }

    if (objectsLoading || loading) {
        return (
            <Stack sx={{ alignItems: "center", py: 12 }}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (!object) {
        return (
            <Stack sx={{ alignItems: "center", py: 12, gap: 2 }}>
                <Typography variant="h6">Object not found</Typography>
                <Button
                    variant="soft-outlined"
                    onClick={() => navigate("/admin/objects")}
                >
                    Open the object builder
                </Button>
            </Stack>
        );
    }

    return (
        <Stack sx={{ height: "100%" }}>
            <PageHeader
                title={
                    <Stack
                        component="span"
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1.25,
                        }}
                    >
                        <ObjectIcon name={object.icon} size={22} />
                        {object.name}
                    </Stack>
                }
                subtitle={
                    board?.pipeline?.name
                        ? `Board · ${board.pipeline.name}`
                        : "Board"
                }
                action={
                    <Stack sx={{ flexDirection: "row", gap: 1.5 }}>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                border: "1px solid var(--border-mid)",
                                borderRadius: "3px",
                                overflow: "hidden",
                            }}
                        >
                            <Button
                                variant="text"
                                startIcon={<TableRowsRounded />}
                                sx={{ height: 42, borderRadius: 0 }}
                                onClick={() => navigate(`/o/${slug}`)}
                            >
                                List
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ViewKanbanRounded />}
                                sx={{ height: 42, borderRadius: 0 }}
                            >
                                Board
                            </Button>
                        </Stack>
                        <Button
                            variant="soft-outlined"
                            startIcon={<AccountTreeRounded />}
                            onClick={() => navigate(`/o/${slug}/pipeline`)}
                        >
                            Pipeline
                        </Button>
                    </Stack>
                }
            />

            {noPipeline ? (
                <Stack sx={{ alignItems: "center", py: 10, gap: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: "'Newsreader', Georgia, serif",
                            fontSize: 26,
                            fontWeight: 500,
                        }}
                    >
                        No pipeline yet
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 14,
                            color: "var(--text-secondary)",
                            maxWidth: 420,
                            textAlign: "center",
                            lineHeight: 1.6,
                        }}
                    >
                        Give {object.name} a pipeline to move records through
                        stages — then this board comes alive.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        loading={creating}
                        startIcon={<AccountTreeRounded />}
                        onClick={handleCreatePipeline}
                    >
                        Create pipeline
                    </Button>
                </Stack>
            ) : (
                <Stack
                    sx={{
                        flexDirection: "row",
                        gap: 2,
                        alignItems: "flex-start",
                        overflowX: "auto",
                        pb: 2,
                    }}
                >
                    {(board?.columns ?? []).map((col) => (
                        <Stack
                            key={col.stage.id}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(col.stage.id);
                            }}
                            onDragLeave={() => setDragOver(null)}
                            onDrop={(e) => onDrop(e, col.stage)}
                            sx={{
                                width: 272,
                                flexShrink: 0,
                                background:
                                    dragOver === col.stage.id
                                        ? "var(--accent-soft)"
                                        : "var(--bg-tint-soft)",
                                border:
                                    dragOver === col.stage.id
                                        ? "1px dashed var(--accent)"
                                        : "1px solid var(--border)",
                                borderRadius: "6px",
                                p: 1.5,
                                gap: 1.25,
                                minHeight: 220,
                                transition: "background 0.15s, border 0.15s",
                            }}
                        >
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 0.5,
                                }}
                            >
                                <Stack
                                    component="span"
                                    sx={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: "2px",
                                        background:
                                            col.stage.color ||
                                            "var(--border-strong)",
                                    }}
                                />
                                <Typography
                                    sx={{ fontSize: 13.5, fontWeight: 600 }}
                                >
                                    {col.stage.name}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={col.records.length}
                                    sx={{
                                        height: 20,
                                        fontSize: 11.5,
                                        background: "var(--bg-secondary)",
                                        border: "1px solid var(--border-mid)",
                                        color: "var(--text-secondary)",
                                    }}
                                />
                            </Stack>

                            {col.records.map((r) => (
                                <RecordCard
                                    key={r.id}
                                    record={r}
                                    titleKey={titleKey}
                                    onDragStart={onDragStart}
                                />
                            ))}

                            {col.records.length === 0 && (
                                <Typography
                                    sx={{
                                        fontSize: 12.5,
                                        color: "var(--text-tertiary)",
                                        textAlign: "center",
                                        py: 2,
                                    }}
                                >
                                    Drop records here
                                </Typography>
                            )}
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
